/* ==========================================================================
   MHM – Meine Hunde Momente | Produktsektion
   Datei: assets/mhm-product.js

   Zuständig für:
     - Farb-, Gravur- und Bundle-Auswahl inkl. Variantenzuordnung
     - Preis-, Ersparnis- und Verfügbarkeitsanzeige
     - Live-Vorschau der Gravur
     - Mengenauswahl, Warenkorb (optional ohne Seitenneuladen)
     - Bildergalerie mit Farbfilter, Sticky-Leiste, Scroll-Effekte

   Es werden keine externen Bibliotheken benötigt.
   ========================================================================== */

(function () {
  'use strict';

  /** Vergleicht Optionswerte unabhängig von Groß-/Kleinschreibung und Leerzeichen. */
  function norm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  class MHMProduct {
    constructor(root) {
      this.root = root;

      const configEl = root.querySelector('[data-mhm-config]');
      if (!configEl) return;

      try {
        this.cfg = JSON.parse(configEl.textContent);
      } catch (err) {
        console.warn('[MHM] Konfiguration konnte nicht gelesen werden.', err);
        return;
      }

      this.money = new Intl.NumberFormat(this.cfg.locale || 'de-DE', {
        style: 'currency',
        currency: this.cfg.currency || 'EUR'
      });

      this.cacheElements();

      this.state = {
        offer: 'single',
        color: this.readCheckedValue(this.colorInputs),
        engraving: this.readCheckedValue(this.engravingInputs) === 'true',
        quantity: 1,
        name: ''
      };

      this.bindEvents();
      this.initGallery();
      this.initReveal();
      this.initSticky();
      this.update();
    }

    /* ------------------------------------------------------------------ */
    /* Elemente einsammeln                                                 */
    /* ------------------------------------------------------------------ */
    cacheElements() {
      const q = (sel) => this.root.querySelector(sel);
      const qa = (sel) => Array.from(this.root.querySelectorAll(sel));

      this.offerInputs = qa('[data-mhm-offer-input]');
      this.offerCards = qa('[data-mhm-offer-card]');
      this.colorInputs = qa('[data-mhm-color-input]');
      this.engravingInputs = qa('[data-mhm-engraving-input]');
      this.engravingField = q('[data-mhm-engraving-field]');
      this.colorLabel = q('[data-mhm-color-label]');

      this.engraveBox = q('[data-mhm-engrave]');
      this.nameInput = q('[data-mhm-name-input]');
      this.namePreview = q('[data-mhm-preview]');
      this.nameCounter = q('[data-mhm-counter]');
      this.nameError = q('[data-mhm-name-error]');

      this.priceEl = q('[data-mhm-price]');
      this.compareEl = q('[data-mhm-compare]');
      this.saveEl = q('[data-mhm-save]');
      this.offerSaveEl = q('[data-mhm-offer-save]');
      this.offerPriceEls = qa('[data-mhm-offer-price]');

      this.qtyInput = q('[data-mhm-qty-input]');
      this.qtyMinus = q('[data-mhm-qty-minus]');
      this.qtyPlus = q('[data-mhm-qty-plus]');

      this.forms = qa('[data-mhm-offer-form]');
      this.message = q('[data-mhm-message]');

      this.sticky = q('[data-mhm-sticky]');
      this.stickyPrice = q('[data-mhm-sticky-price]');
      this.stickyName = q('[data-mhm-sticky-name]');
      this.stickyAdd = q('[data-mhm-sticky-add]');
      this.buyArea = q('.mhm-buy');

      this.mediaFlag = q('[data-mhm-media-flag]');
      this.slides = qa('[data-mhm-slide]');
      this.thumbs = qa('[data-mhm-thumb]');
    }

    readCheckedValue(inputs) {
      if (!inputs || !inputs.length) return '';
      const checked = inputs.find((input) => input.checked);
      return checked ? checked.value : inputs[0].value;
    }

    /* ------------------------------------------------------------------ */
    /* Ereignisse                                                          */
    /* ------------------------------------------------------------------ */
    bindEvents() {
      this.offerInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.state.offer = input.value;
          this.update();
        });
      });

      this.colorInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.state.color = input.value;
          this.showColorImage(input.value);
          this.update();
        });
      });

      this.engravingInputs.forEach((input) => {
        input.addEventListener('change', () => {
          this.state.engraving = input.value === 'true';
          this.update();
          if (this.state.engraving && this.nameInput) this.nameInput.focus();
        });
      });

      if (this.nameInput) {
        this.nameInput.addEventListener('input', () => {
          this.state.name = this.nameInput.value;
          this.updateNamePreview();
          this.clearNameError();
          this.syncForms();
        });
      }

      if (this.qtyInput) {
        this.qtyInput.addEventListener('change', () => this.setQuantity(this.qtyInput.value));
        this.qtyMinus.addEventListener('click', () => this.setQuantity(this.state.quantity - 1));
        this.qtyPlus.addEventListener('click', () => this.setQuantity(this.state.quantity + 1));
      }

      // Formulare: Validierung und optionaler Warenkorb ohne Neuladen
      this.forms.forEach((wrapper) => {
        const form = wrapper.querySelector('form');
        if (!form) return;
        form.addEventListener('submit', (event) => this.onSubmit(event, form));
      });

      if (this.stickyAdd) {
        this.stickyAdd.addEventListener('click', () => {
          const button = this.activeForm() && this.activeForm().querySelector('[data-mhm-add]');
          if (button && !button.disabled) button.click();
        });
      }
    }

    /* ------------------------------------------------------------------ */
    /* Variantenlogik                                                      */
    /* ------------------------------------------------------------------ */

    /** Sucht die Variante, die zur aktuellen Auswahl passt. */
    findVariant(offerKey) {
      const offer = this.cfg.offers[offerKey];
      if (!offer) return null;

      const options = (offer.options || []).map(norm);
      const colorIndex = options.indexOf(norm(this.cfg.colorOption));
      const engravingIndex = options.indexOf(norm(this.cfg.engravingOption));

      // Beim Bundle ist die Gravur enthalten – dort wird nicht nach ihr gefiltert.
      const wantEngraving = offerKey === 'bundle' ? null : this.state.engraving;

      const match = offer.variants.find((variant) => {
        const values = variant.options || [variant.option1, variant.option2, variant.option3];

        if (colorIndex > -1 && this.state.color) {
          if (norm(values[colorIndex]) !== norm(this.state.color)) return false;
        }
        if (engravingIndex > -1 && wantEngraving !== null) {
          const isEngraved = norm(values[engravingIndex]) === norm(this.cfg.engravingYes);
          if (isEngraved !== wantEngraving) return false;
        }
        return true;
      });

      return match || null;
    }

    savingsPrefix() {
      return (this.cfg.strings && this.cfg.strings.savings) || 'Du sparst';
    }

    /** Ist für die aktuelle Auswahl ein Hundename nötig? */
    needsName() {
      if (this.state.offer === 'bundle') return !!this.cfg.bundleEngravingIncluded;
      return this.state.engraving;
    }

    activeForm() {
      return this.root.querySelector('[data-mhm-offer-form="' + this.state.offer + '"]');
    }

    /* ------------------------------------------------------------------ */
    /* Anzeige aktualisieren                                               */
    /* ------------------------------------------------------------------ */
    update() {
      const variant = this.findVariant(this.state.offer);

      // Preis, Streichpreis, Ersparnis
      if (this.priceEl) {
        this.priceEl.textContent = variant ? this.money.format(variant.price / 100) : '–';
      }

      const hasCompare = variant && variant.compare_at_price && variant.compare_at_price > variant.price;

      if (this.compareEl) {
        this.compareEl.hidden = !hasCompare;
        if (hasCompare) this.compareEl.textContent = this.money.format(variant.compare_at_price / 100);
      }

      if (this.saveEl) {
        this.saveEl.hidden = !hasCompare;
        if (hasCompare) {
          const saved = (variant.compare_at_price - variant.price) / 100;
          this.saveEl.textContent = this.savingsPrefix() + ' ' + this.money.format(saved);
        }
      }

      // Preise in den Kaufoptionen
      this.offerPriceEls.forEach((el) => {
        const offerVariant = this.findVariant(el.dataset.mhmOfferPrice);
        el.textContent = offerVariant ? this.money.format(offerVariant.price / 100) : '–';
      });

      // Ersparnis-Badge auf der Bundle-Karte
      if (this.offerSaveEl) {
        const bundleVariant = this.findVariant('bundle');
        const canSave = bundleVariant && bundleVariant.compare_at_price > bundleVariant.price;
        this.offerSaveEl.hidden = !canSave;
        if (canSave) {
          const saved = (bundleVariant.compare_at_price - bundleVariant.price) / 100;
          this.offerSaveEl.textContent = this.savingsPrefix() + ' ' + this.money.format(saved);
        }
      }

      // Farbname im Label
      if (this.colorLabel && this.state.color) this.colorLabel.textContent = this.state.color;

      // Gravur-Auswahl nur beim Einzelprodukt anzeigen
      if (this.engravingField) this.engravingField.hidden = this.state.offer === 'bundle';

      // Namensfeld ein-/ausblenden
      if (this.engraveBox) {
        const show = this.needsName();
        this.engraveBox.hidden = !show;
        if (show) this.updateNamePreview();
      }

      // Bundle-Badge über dem Bild
      if (this.mediaFlag) this.mediaFlag.hidden = this.state.offer !== 'bundle';

      // Sticky-Leiste
      if (this.stickyPrice && variant) this.stickyPrice.textContent = this.money.format(variant.price / 100);
      if (this.stickyName) {
        const card = this.root.querySelector('[data-mhm-offer-card="' + this.state.offer + '"] .mhm-offer__name');
        if (card) this.stickyName.textContent = card.textContent.trim();
      }

      this.setActiveClasses();
      this.syncForms(variant);
    }

    setActiveClasses() {
      this.offerCards.forEach((card) => {
        card.classList.toggle('is-active', card.dataset.mhmOfferCard === this.state.offer);
      });
      this.colorInputs.forEach((input) => {
        const label = input.closest('.mhm-swatch');
        if (label) label.classList.toggle('is-active', input.checked);
      });
      this.forms.forEach((wrapper) => {
        wrapper.hidden = wrapper.dataset.mhmOfferForm !== this.state.offer;
      });
    }

    /** Schreibt Variante, Menge und Gravurname in beide Formulare. */
    syncForms(activeVariant) {
      this.forms.forEach((wrapper) => {
        const key = wrapper.dataset.mhmOfferForm;
        const variant = key === this.state.offer && activeVariant ? activeVariant : this.findVariant(key);

        const idInput = wrapper.querySelector('[data-mhm-variant-id]');
        const qtyInput = wrapper.querySelector('[data-mhm-form-qty]');
        const propInput = wrapper.querySelector('[data-mhm-form-property]');
        const button = wrapper.querySelector('[data-mhm-add]');
        const label = wrapper.querySelector('[data-mhm-add-label]');
        const dynamic = wrapper.querySelector('.mhm-dynamic');

        if (idInput) {
          idInput.value = variant ? variant.id : '';
          idInput.disabled = !variant;
        }
        if (qtyInput) qtyInput.value = this.state.quantity;

        // Gravurname nur mitsenden, wenn er gebraucht wird und ausgefüllt ist
        if (propInput) {
          const name = this.state.name.trim();
          const send = key === 'bundle' ? !!this.cfg.bundleEngravingIncluded : this.state.engraving;
          propInput.disabled = !(send && name.length > 0);
          propInput.value = name;
        }

        const available = !!(variant && variant.available);
        if (button) button.disabled = !available;
        if (label) {
          label.textContent = available
            ? label.dataset.mhmLabelDefault || 'In den Warenkorb'
            : this.cfg.strings.soldOut;
        }

        // „Jetzt kaufen“ sperren, solange ein Pflicht-Gravurname fehlt
        if (dynamic) {
          const blocked =
            key === this.state.offer &&
            this.needsName() &&
            this.cfg.engravingRequired &&
            !this.state.name.trim();
          dynamic.classList.toggle('is-disabled', blocked || !available);
        }
      });
    }

    setQuantity(value) {
      let qty = parseInt(value, 10);
      if (isNaN(qty) || qty < 1) qty = 1;
      if (qty > 99) qty = 99;
      this.state.quantity = qty;
      if (this.qtyInput) this.qtyInput.value = qty;
      this.syncForms();
    }

    /* ------------------------------------------------------------------ */
    /* Gravur-Vorschau                                                     */
    /* ------------------------------------------------------------------ */
    updateNamePreview() {
      if (!this.namePreview) return;
      const name = this.state.name.trim();
      const placeholder = this.nameInput ? this.nameInput.getAttribute('placeholder') : '';
      this.namePreview.textContent = name || placeholder;
      this.namePreview.classList.toggle('is-set', name.length > 0);
      if (this.nameCounter) this.nameCounter.textContent = String(name.length);
    }

    showNameError(text) {
      if (this.nameError) {
        this.nameError.textContent = text;
        this.nameError.hidden = false;
      }
      if (this.nameInput) {
        this.nameInput.classList.add('is-error');
        this.nameInput.focus();
      }
    }

    clearNameError() {
      if (this.nameError) this.nameError.hidden = true;
      if (this.nameInput) this.nameInput.classList.remove('is-error');
    }

    /* ------------------------------------------------------------------ */
    /* Warenkorb                                                           */
    /* ------------------------------------------------------------------ */
    onSubmit(event, form) {
      // 1. Pflichtfeld Gravurname prüfen
      if (this.needsName() && this.cfg.engravingRequired && !this.state.name.trim()) {
        event.preventDefault();
        this.showNameError(this.cfg.strings.nameMissing);
        return;
      }

      // 2. Ohne AJAX übernimmt Shopify den normalen Formularversand
      if (!this.cfg.ajax) return;

      event.preventDefault();
      const button = form.querySelector('[data-mhm-add]');
      if (button) button.classList.add('is-loading');
      this.hideMessage();

      const url = (this.cfg.cartAddUrl || '/cart/add') + '.js';

      fetch(url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, data })))
        .then((result) => {
          if (!result.ok) {
            this.showMessage(result.data.description || this.cfg.strings.error, true);
            return;
          }
          this.showMessage(this.cfg.strings.added, false, true);
          this.refreshThemeCart();
        })
        .catch(() => this.showMessage(this.cfg.strings.error, true))
        .finally(() => {
          if (button) button.classList.remove('is-loading');
        });
    }

    /** Bittet das Theme, seinen Warenkorb neu zu laden (Dawn & Co.). */
    refreshThemeCart() {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:build'));

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        try {
          drawer.open();
        } catch (err) {
          /* Theme ohne Drawer – Meldung reicht aus */
        }
      }
    }

    showMessage(text, isError, withLink) {
      if (!this.message) return;
      this.message.textContent = text;
      if (withLink) {
        const link = document.createElement('a');
        link.href = this.cfg.cartUrl || '/cart';
        link.textContent = ' Warenkorb ansehen';
        this.message.appendChild(link);
      }
      this.message.classList.toggle('is-error', !!isError);
      this.message.hidden = false;
    }

    hideMessage() {
      if (this.message) this.message.hidden = true;
    }

    /* ------------------------------------------------------------------ */
    /* Galerie                                                             */
    /* ------------------------------------------------------------------ */
    initGallery() {
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.showSlide(thumb.dataset.mhmThumb));
      });
    }

    showSlide(index) {
      this.slides.forEach((slide) => {
        slide.classList.toggle('is-active', slide.dataset.mhmSlide === String(index));
      });
      this.thumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.mhmThumb === String(index));
      });
    }

    /** Springt zum ersten Bild, dessen Alt-Text die gewählte Farbe enthält. */
    showColorImage(color) {
      const key = norm(color).indexOf('mint') > -1 ? 'mint' : norm(color).indexOf('pink') > -1 ? 'pink' : '';
      if (!key) return;
      const match = this.slides.find((slide) => slide.dataset.mhmSlideColor === key);
      if (match) this.showSlide(match.dataset.mhmSlide);
    }

    /* ------------------------------------------------------------------ */
    /* Scroll-Effekte                                                      */
    /* ------------------------------------------------------------------ */
    initReveal() {
      const targets = this.root.querySelectorAll('[data-mhm-reveal]');
      if (!targets.length || !('IntersectionObserver' in window)) {
        targets.forEach((el) => el.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12 }
      );
      targets.forEach((el) => observer.observe(el));
    }

    initSticky() {
      if (!this.sticky) return;

      // Die Leiste wird im HTML mit "hidden" ausgeliefert, damit sie beim
      // Laden nicht kurz aufblitzt. Erst hier wird sie freigegeben – die
      // Sichtbarkeit steuert danach die Medienabfrage im CSS (nur Mobil).
      this.sticky.hidden = false;

      if (!this.buyArea || !('IntersectionObserver' in window)) {
        this.sticky.classList.add('is-visible');
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.sticky.classList.toggle('is-visible', !entry.isIntersecting);
          });
        },
        { rootMargin: '0px 0px -40px 0px' }
      );
      observer.observe(this.buyArea);
    }
  }

  /* -------------------------------------------------------------------- */
  /* Start                                                                 */
  /* -------------------------------------------------------------------- */
  function init(scope) {
    (scope || document).querySelectorAll('[data-mhm-root]').forEach((root) => {
      if (root.dataset.mhmReady === 'true') return;
      root.dataset.mhmReady = 'true';
      new MHMProduct(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  // Damit die Sektion auch im Theme-Editor sofort funktioniert
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})(); 